<template>
  <section>
    <nav class="level">
      <div class="level-left">
        <div class="level-item">
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.activation_status') }}
              </span>
            </p>
            <b-select v-model="filterStatus" @input="onFilterStatus">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="true">{{ $t('attributes.shelve_on') }}</option>
              <option value="false">{{ $t('attributes.shelve_off') }}</option>
            </b-select>
          </b-field>
        </div>
      </div>

      <div class="level-right">
        <div class="level-item">
          <b-field grouped>
            <button class="button field is-info" @click="onSelectDisplayMode">
              <span>{{
                $t('attributes.setting') + $t('attributes.regular')
              }}</span>
            </button>
            <button class="button field is-primary" @click="onAdd">
              <b-icon icon="plus"></b-icon>
              <span>{{ $t('attributes.add') + $t('attributes.banner') }}</span>
            </button>
          </b-field>
        </div>
      </div>
    </nav>
    <b-table
      :data="items"
      :loading="isLoading"
      :mobile-cards="false"
      :row-class="setRowClass"
      hoverable
      narrowed
      bordered
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="sort"
        :label="$t('attributes.sort')"
        width="75"
        sortable
        numeric
        v-slot="props"
      >
        {{ props.row.sort }}
      </b-table-column>

      <b-table-column
        field="status"
        :label="$t('attributes.activation_status')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <b-tag
          :type="props.row.status ? 'is-info' : 'is-danger'"
          size="is-medium"
        >
          {{ activeStatus(props.row.status) }}
        </b-tag>
      </b-table-column>

      <b-table-column
        field="start_date"
        :label="$t('attributes.start_date')"
        width="120"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.start_date }}
      </b-table-column>

      <b-table-column
        field="end_date"
        :label="$t('attributes.end_date')"
        width="120"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.end_date }}
      </b-table-column>

      <b-table-column
        :label="$t('attributes.picture')"
        width="120"
        sortable
        centered
        v-slot="props"
      >
        <a
          v-if="props.row.picture"
          :href="bannerImage(props.row.picture)"
          @click.prevent="onClickImage(props.row.picture)"
        >
          <figure class="image">
            <img
              :src="bannerImage(props.row.picture)"
              :alt="props.row.picture_alt"
              style="width: 120px; margin: auto;"
            />
          </figure>
        </a>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.mobile_version')"
        width="90"
        sortable
        centered
        v-slot="props"
      >
        <a
          v-if="props.row.mobile_picture"
          :href="bannerImage(props.row.mobile_picture)"
          @click.prevent="onClickImage(props.row.mobile_picture)"
        >
          <figure class="image">
            <img
              :src="bannerImage(props.row.mobile_picture)"
              :alt="props.row.picture_alt"
              style="width: 64px; margin: auto;"
            />
          </figure>
        </a>
      </b-table-column>

      <b-table-column
        field="name"
        :label="$t('attributes.name')"
        sortable
        v-slot="props"
      >
        <p v-html="props.row.name"></p>
      </b-table-column>

      <b-table-column
        field="has_video"
        :label="$t('attributes.has_video')"
        width="140"
        sortable
        centered
        v-slot="props"
      >
        {{ whetherStatus(props.row.has_video) }}
      </b-table-column>

      <b-table-column
        field="url"
        :label="$t('attributes.url')"
        width="100"
        sortable
        centered
        v-slot="props"
      >
        <a v-if="props.row.url" :href="props.row.url" target="_blank">
          {{ props.row.has_video ? '影片連結' : '外連' }}
        </a>
      </b-table-column>

      <b-table-column :width="80" label="功能" centered v-slot="props">
        <div class="buttons">
          <b-button type="is-primary" @click.prevent="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['escape', 'x']"
      :width="1200"
      has-modal-card
    >
      <banner-form :banner="selectedItem" @send="onSend" />
    </b-modal>

    <b-modal
      :active.sync="isConfigFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <config-form />
    </b-modal>

    <b-modal :active.sync="isImageModalActive">
      <p class="image">
        <img :src="selectedImageUrl" alt="" />
      </p>
    </b-modal>
  </section>
</template>

<script>
import { HOME_CAROUSELS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import BannerForm from './BannerForm'
import ConfigForm from './ConfigForm'

export default {
  name: 'BannerList',
  components: { BannerForm, ConfigForm },

  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,

      filterStatus: this.query.status || '',
      sortField: this.query.sort || 'start_date',
      sortOrder: this.query.order || 'desc',
      defaultSortOrder: 'asc',
      page: this.query.page || 1,
      perPage: this.query.page_size || 10,

      isConfigFormModalActive: false,

      isFormModalActive: false,
      sending: false,

      isImageModalActive: false,
      selectedImageUrl: null
    }
  },
  watch: {
    query(query) {
      this.getQuery(query)
      this.loadItems()
    }
  },
  computed: {
    isLoading() {
      return this.loadingItems
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    async loadItems() {
      try {
        const api = `${HOME_CAROUSELS_API}`
        const params = {
          active: this.filterStatus && this.filterStatus === 'true',
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('banner', e)
      } finally {
        this.loadingItems = false
      }
    },
    getQuery(query) {
      this.filterStatus = query.status || ''
      this.sortField = query.sort || 'sort'
      this.sortOrder = query.order || 'asc'
      this.page = query.page || 1
      this.perPage = query.page_size || 10
    },
    updateQuery() {
      const query = {
        status: this.filterStatus,
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilterStatus() {
      this.updateQuery()
    },
    onPageChange(page) {
      this.page = page

      this.updateQuery()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order

      this.updateQuery()
    },
    setRowClass(row) {
      if (!row.status) {
        return 'is-off'
      } else {
        return this.checkDateValid(row.start_date, row.end_date)
          ? 'is-valid'
          : 'is-invalid'
      }
    },
    checkDateValid(start_date, end_date) {
      const nowDate = new Date()
      return (
        Date.parse(start_date) <= nowDate && Date.parse(end_date) >= nowDate
      )
    },
    onSelectDisplayMode() {
      this.isConfigFormModalActive = true
    },
    onAdd() {
      this.selectedItem = {
        id: null,
        sort: 1,
        status: true,
        name: '',
        description: '',
        picture: null,
        mobile_picture: null,
        picture_alt: '',
        has_video: false,
        youtube_id: null,
        url: '',
        start_date: null,
        end_date: null
      }
      this.isFormModalActive = true
    },
    onModify(item) {
      this.selectedItem = item
      this.isFormModalActive = true
    },
    onSend(item) {
      if (item.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    async createItem(item) {
      try {
        const api = `${HOME_CAROUSELS_API}`

        this.sending = true
        await this.axios.post(api, item)
        this.isFormModalActive = false
        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${HOME_CAROUSELS_API}/${item.id}`

        this.sending = true
        const res = await this.axios.put(api, item)
        const _item = res.data.data

        const idx = this.items.findIndex(s => s.id === _item.id)
        const items = [...this.items]
        items[idx] = _item
        this.items = items.sort((t1, t2) => t1.sort - t2.sort)

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = this.bannerImage(url)
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
